/** 테이블 셀 Base 컴포넌트 — 순수 렌더링만 담당, 편집 로직 없음 */

import { cn } from "@/lib/utils";
import { extendedTagClass } from "@/components/extended-tag-style";
import { ATTR } from "@/utils/dom-attrs";
import { Sigma, TriangleAlert } from "lucide-react";
import { TooltipWrapper } from "@/components/common/tooltip";
import { IxdTable, type Period } from "@/components/ixd-table";
import type { CellNoticeTone } from "@/components/ixd-table";
import type { TableColumn, PeriodType } from "@/apis/compare/types";
import {
  buildCellIdFromTableColumn,
  getHeaderLookupKeys,
  parseCellId,
} from "@/utils/ixd-cell-model";

/** 외부에서 TableCellColumn 이름으로 참조할 수 있도록 re-export */
export type TableCellColumn = TableColumn;

/**
 * PeriodType → IxdTable Period 매핑 (당기 → amber, 전기/전전기 → slate)
 */
const periodTypeToPeriod = (pt: PeriodType): Period =>
  pt === "CURRENT" ? "당기" : "전기";

/**
 * 공백 없는 숫자 토큰 — 공백이 있으면 이미 줄바꿈 기회가 있어 숫자 중간이 끊기지 않는다.
 *
 * 앞의 lookahead 로 숫자를 최소 하나 요구한다(`-`, `()`, `...` 만으로는 매칭되지 않는다).
 * `△▲▽▼` 와 U+2212 는 공시 표에서 쓰는 음수 부호라 숫자 표기의 일부로 본다.
 *
 * 자릿수 상한은 두지 않는다. 인라인 min-width 가 max-width 를 이기므로 값이 길면
 * 열이 그만큼 넓어질 뿐이고, 넘친 폭은 `TableScrollArea` 가 가로 스크롤로 흡수한다.
 * 길이로 폴백을 두면 정확히 "큰 숫자만 다시 끊기는" 상태가 된다.
 */
const NUMERIC_TOKEN_PATTERN = /^(?=[^\d]*\d)[\d.,\-+()%△▲▽▼−]+$/;

/**
 * 숫자로 읽히는 표시값 판정.
 *
 * schemaType 이 아니라 표시 텍스트로 판정하는 이유: `적수` 처럼 data type 이
 * stringItemType 인 셀도 값은 숫자라 끊기면 자릿수를 오독하게 된다.
 */
export const isNumericLikeText = (text: string): boolean => {
  const token = text.trim();
  return token.length > 0 && NUMERIC_TOKEN_PATTERN.test(token);
};

/** 11px 폰트 숫자 실측 폭 6.85px 의 상한 — 과대추정 방향이라 안전 측이다. */
const NUMERIC_CHAR_WIDTH_PX = 7;
/** td 기본 최소 폭 — 아래 td className 의 `min-w-[120px]`(및 th 의 같은 값)와 함께 바꿀 것. */
const CELL_MIN_WIDTH_PX = 120;

/**
 * 값 폭 밖에서 셀이 먹는 여백 — `ixd-table.tsx` IxdCell 의 조건부 자식 때문에 세 레짐이다.
 *
 * 공통은 패딩 16 + 보더. 여기에
 * - 아이콘(Σ/⚠) 있음: ChipIcon 18 + gap 6
 * - 아이콘 없고 좌측정렬: spacer(size-4) 16 + gap 6  ← `적수` 같은 문자열 타입 숫자 셀
 * - 아이콘 없고 우측정렬: spacer 도 아이콘도 없음
 *
 * 하나로 뭉뚱그리면 아이콘 셀에서 폭이 모자라 열이 캡으로 눌린 상황의 보장이 얇아진다.
 */
export const numericCellChrome = (
  hasIcon: boolean,
  alignRight: boolean,
): number => (hasIcon ? 42 : alignRight ? 18 : 39);

/**
 * 숫자 셀이 자기 값보다 좁아지지 않도록 보장하는 td 최소 폭.
 *
 * px min-width 는 명시 width/max-width 캡을 이기므로, 열이 수요만큼 자라는
 * 경우든 무언가가 열을 눌러 놓은 경우든 값 전체가 셀 안에 들어간다.
 * 키워드형(max-content/fit-content)은 table-cell 에서 무시되어 쓸 수 없다.
 */
export const numericCellMinWidth = (length: number, chromePx: number): number =>
  Math.max(
    CELL_MIN_WIDTH_PX,
    Math.ceil(length * NUMERIC_CHAR_WIDTH_PX) + chromePx,
  );

export interface TableCellProps {
  column: TableColumn;
  periodType: PeriodType;
  /** DOM 유니크 ID (셀 선택용 — `useTableCellSelection` 이 이 id 로 셀을 찾는다) */
  uniqueId?: string;
  referenceBalance?: "DEBIT" | "CREDIT" | null;
  labelMap?: Map<string, string>;
  /** 표시할 텍스트 (기본: column.text) */
  displayText?: string;
  /** 자동합으로 기대되는 표시값. 실제 셀 값은 바꾸지 않고 hover tooltip에만 표시한다. */
  expectedAutoSumDisplayText?: string;
  /** td 셀 내부 콘텐츠 override (기본: displayText + autosum 아이콘) */
  cellContent?: React.ReactNode;
  /** 예상 자동합 값을 현재 셀에 적용 */
  onApplyExpectedAutoSum?: () => void;
  /** 같은 metadata identity를 가진 다른 셀 개수 */
  relatedCellIdentityCount?: number;
  /** 사용자가 직접 수정하거나 붙여넣은 셀의 우상단 주황색 표식 */
  showManualEditMarker?: boolean;
  /** 반영하지 않은 셀 표식 */
  showNoticeMarker?: boolean;
  noticeTone?: CellNoticeTone;
  /** 숫자 스키마 셀 우측정렬 (Σ 칩은 왼쪽 고정) */
  alignRight?: boolean;
  /** 값 아래 한 줄 안내 — 셀 색·모서리 표식이 무슨 뜻인지 적는다 */
  footnote?: string;
  /** 추가 className (편집 상태 스타일 등) */
  cellClassName?: string;
  /** td에 추가할 props (이벤트 핸들러, data-* 속성 등) */
  tdProps?: React.TdHTMLAttributes<HTMLTableCellElement>;
}

/**
 * Base TableCell — IxdTable 디자인 시스템 기반
 *
 * - th: IxdTable.Header (period 색상, DebitCreditChip, negated)
 * - td: IxdTable.Cell (matchResult, autosum 아이콘 칩)
 * - 편집 로직은 EditableTableCell에서 cellContent/tdProps로 주입
 */
const TableCell = ({
  column,
  periodType,
  uniqueId,
  referenceBalance,
  labelMap,
  displayText,
  expectedAutoSumDisplayText,
  cellContent,
  onApplyExpectedAutoSum,
  relatedCellIdentityCount,
  showManualEditMarker,
  showNoticeMarker,
  noticeTone,
  alignRight = false,
  footnote,
  cellClassName,
  tdProps,
}: TableCellProps) => {
  const Tag = column.tagName;
  const text = displayText ?? column.text;
  const isExtended = column.attributes.extended;

  if (Tag === "th") {
    const isRowHeader = column.scope === "row";
    // DSD 헤더 클릭 → 대응 IXD 헤더 포커싱용. lineItem/member 는 element id 로,
    // 기간 헤더(col)는 periodType 으로 매칭한다.
    const headerIds = getHeaderLookupKeys(column).join(" ");
    // 헤더 hover 시 표준(택소노미) 라벨을 보여준다 — 표시값과 같아도 항상 노출.
    const standardLabel = column.standardLabel || null;

    const calcWeightSign =
      column.autoSumWeight != null
        ? column.autoSumWeight > 0
          ? "+"
          : "-"
        : undefined;

    // IXD .frm 에 저장된 계정과목별 주석번호(NoteNumber) — 보조색으로 라벨과 구분한다.
    // 백엔드가 row header th 에만 싣지만, 렌더도 행 헤더로 한정해 이중 방어한다.
    const noteNumber = isRowHeader ? column.noteNumber?.trim() : undefined;
    const noteSpan = noteNumber ? (
      <span className="text-label-alternative whitespace-pre-wrap break-words">
        {` (주${noteNumber})`}
      </span>
    ) : null;

    const headerCell = (
      <IxdTable.Header
        period={periodTypeToPeriod(periodType)}
        isNegated={isRowHeader && column.negated}
        chipPlacement="bottom"
        showDebitCreditChip={isRowHeader && column.balance !== null}
        debitCreditValue={column.balance === "CREDIT" ? "대변" : "차변"}
        calcWeightSign={isRowHeader ? calcWeightSign : undefined}
        scope={(column.scope as "row" | "col" | undefined) || undefined}
        rowSpan={column.rowspan > 1 ? column.rowspan : undefined}
        colSpan={column.colspan > 1 ? column.colspan : undefined}
        data-header-ids={headerIds || undefined}
        data-header-period={column.scope === "col" ? periodType : undefined}
        data-auto-sum-value-different={
          column.autoSumValueDifferent ? "true" : undefined
        }
        data-auto-sum-value-same={column.autoSumValueSame ? "true" : undefined}
        className="min-w-[120px] max-w-[240px]"
      >
        <span
          className={cn(
            "whitespace-pre-wrap break-words [overflow-wrap:anywhere]",
            isExtended && extendedTagClass("th"),
          )}
        >
          {column.text.replace("[구성요소]", "")}
        </span>
        {noteSpan}
      </IxdTable.Header>
    );

    // 별칭 헤더는 셀 전체 hover 로 표준 라벨 tooltip 노출 (asChild 라 th 가 그대로 트리거)
    return standardLabel ? (
      <TooltipWrapper label={`표준 명칭: ${standardLabel}`} side="top">
        {headerCell}
      </TooltipWrapper>
    ) : (
      headerCell
    );
  }

  // autosum 아이콘 결정
  let rightIcon: React.ReactNode = undefined;
  let iconChipClassName = "";
  if (column.autoSumValueSame) {
    rightIcon = <Sigma />;
    iconChipClassName = "bg-orange-500 text-static-white";
  }
  if (column.autoSumValueDifferent) {
    rightIcon = <TriangleAlert />;
    iconChipClassName = "";
  }

  // td — 기본 콘텐츠: displayText
  const content = cellContent ?? text;
  const isEmptyValueCell = String(text).trim() === "";

  // 숫자 값 셀은 중간에서 끊지 않고(nowrap), 값 폭만큼 열을 넓힌다(minWidth).
  // 두 값 div 분기가 같은 클래스를 쓰도록 wrap 클래스는 여기서 한 번만 정한다.
  const isNumericValue = isNumericLikeText(text);
  const valueWrapClass = isNumericValue
    ? "whitespace-nowrap"
    : "break-words [overflow-wrap:anywhere]";
  const numericMinWidthPx = isNumericValue
    ? numericCellMinWidth(
        text.trim().length,
        numericCellChrome(Boolean(rightIcon), alignRight),
      )
    : undefined;

  // tooltip 빌드
  let tooltipNode: React.ReactNode = null;
  const shouldShowExpectedAutoSum =
    column.expectedAutoSumValue !== null &&
    column.expectedAutoSumValue !== undefined &&
    expectedAutoSumDisplayText !== undefined;
  const applyExpectedAutoSum = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onApplyExpectedAutoSum?.();
  };

  const autoSumActionLabel = onApplyExpectedAutoSum
    ? "더블클릭하면 예상 자동합을 셀에 적용합니다."
    : "예상 자동합";
  const autoSumIconNode =
    rightIcon && shouldShowExpectedAutoSum ? (
      <TooltipWrapper label={autoSumActionLabel} side="top">
        <button
          type="button"
          className="flex size-2 items-center justify-center rounded-[2px] outline-none focus-visible:ring-2 focus-visible:ring-primary-base [&>svg]:size-full [--lucide-stroke-width:0.8px]"
          aria-label={autoSumActionLabel}
          onDoubleClick={
            onApplyExpectedAutoSum ? applyExpectedAutoSum : undefined
          }
        >
          {rightIcon}
        </button>
      </TooltipWrapper>
    ) : (
      rightIcon
    );
  const shouldShowIdentityCount =
    relatedCellIdentityCount !== undefined && relatedCellIdentityCount > 0;
  // 같은 identity 가 다른 위치에도 있어 클릭 시 linkage popover/modal 이 열리는 셀.
  // 우상단 모서리에 연동 표식을 단다. (getRelatedIdentityCount = 전체-1 이므로 > 0)
  const hasLinkedCells = shouldShowIdentityCount;

  // 라인아이템 라벨 — tooltip 과 match-navigation floating panel 양쪽에서 사용한다.
  // navigation 패널은 DOM 의 data-line-item-label 속성만 읽으므로 여기서 한 번
  // 계산해 attribute 로 노출한다.
  //
  // contextKey 는 cellId 와 동일한 4-segment 정규형(`{periodType}:{lineItemId}:
  // {axis}:{accumulatedType}`)을 따른다. column.id 가 있으면 그것을, 없으면
  // contextKey 를 periodType 으로 정규화해 canonical cellId 를 얻는다.
  const canonicalCellId = buildCellIdFromTableColumn(periodType, column);
  let lineItemLabel: string | undefined;
  let axisLabels: string[] = [];
  if (canonicalCellId && labelMap) {
    const { lineItemId, axisIds } = parseCellId(canonicalCellId);
    if (lineItemId) {
      lineItemLabel = labelMap.get(lineItemId) ?? lineItemId;
      axisLabels = axisIds.map((id) => labelMap.get(id) ?? id);
    }
  }
  if (lineItemLabel) {
    tooltipNode = (
      <div className="flex flex-col gap-1">
        <div className="flex items-start gap-1.5">
          <span className="caption-2-medium text-neutral-400 shrink-0">
            라인아이템
          </span>
          <span className="caption-2-medium text-neutral-700">
            {lineItemLabel}
          </span>
        </div>
        {axisLabels.length > 0 && (
          <div className="flex items-start gap-1.5">
            <span className="caption-2-medium text-neutral-400 shrink-0">
              멤버
            </span>
            <span className="caption-2-medium text-neutral-700">
              {axisLabels.join(", ")}
            </span>
          </div>
        )}
        {shouldShowIdentityCount && (
          <div className="flex items-start gap-1.5">
            <span className="caption-2-medium text-neutral-400 shrink-0">
              다른 동일 항목
            </span>
            <span className="caption-2-medium text-neutral-700">
              {relatedCellIdentityCount}개
            </span>
          </div>
        )}
        {shouldShowExpectedAutoSum && (
          <div className="flex items-start gap-1.5">
            <span className="caption-2-medium text-neutral-400 shrink-0">
              예상 자동합
            </span>
            <span className="caption-2-medium text-neutral-700">
              {expectedAutoSumDisplayText}
            </span>
          </div>
        )}
      </div>
    );
  }

  if (!tooltipNode && (shouldShowIdentityCount || shouldShowExpectedAutoSum)) {
    tooltipNode = (
      <div className="flex flex-col gap-1">
        {shouldShowIdentityCount && (
          <div className="flex items-start gap-1.5">
            <span className="caption-2-medium text-neutral-400 shrink-0">
              다른 동일 항목
            </span>
            <span className="caption-2-medium text-neutral-700">
              {relatedCellIdentityCount}개
            </span>
          </div>
        )}
        {shouldShowExpectedAutoSum && (
          <div className="flex items-start gap-1.5">
            <span className="caption-2-medium text-neutral-400 shrink-0">
              예상 자동합
            </span>
            <span className="caption-2-medium text-neutral-700">
              {expectedAutoSumDisplayText}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <IxdTable.Cell
      matchResult="default"
      iconChipClassName={iconChipClassName}
      icon={autoSumIconNode}
      showLinkageMarker={hasLinkedCells}
      showManualEditMarker={showManualEditMarker}
      showNoticeMarker={showNoticeMarker}
      noticeTone={noticeTone}
      alignRight={alignRight}
      id={uniqueId || undefined}
      {...{
        [ATTR.cellId]: column.id || undefined,
        [ATTR.lineItemLabel]: lineItemLabel || undefined,
      }}
      rowSpan={column.rowspan > 1 ? column.rowspan : undefined}
      colSpan={column.colspan > 1 ? column.colspan : undefined}
      data-auto-sum-value-different={
        column.autoSumValueDifferent ? "true" : undefined
      }
      data-auto-sum-value-same={column.autoSumValueSame ? "true" : undefined}
      data-expected-auto-sum-value-display={expectedAutoSumDisplayText}
      className={cn("min-w-[120px] max-w-[240px]", cellClassName)}
      {...tdProps}
      style={{ minWidth: numericMinWidthPx, ...tdProps?.style }}
    >
      {tooltipNode &&
      (shouldShowIdentityCount ||
        shouldShowExpectedAutoSum ||
        !isEmptyValueCell) ? (
        <TooltipWrapper
          label={tooltipNode}
          side="top"
          align="start"
          styles={{ contentStyle: "p-2 max-w-[320px]" }}
        >
          <div
            className={cn(
              "min-h-[1em] w-full cursor-default",
              valueWrapClass,
              alignRight && "text-right",
              isExtended && extendedTagClass("td"),
            )}
          >
            {content}
          </div>
        </TooltipWrapper>
      ) : (
        <div
          className={cn(
            "min-h-[1em] w-full min-w-0",
            valueWrapClass,
            alignRight && "text-right",
            isExtended && extendedTagClass("td"),
          )}
        >
          {content}
        </div>
      )}
      {/* 셀 색·삼각형의 뜻은 셀에 상주한다 — tooltip 은 열어 보기 전까지 숫자만 보인다.
          좁은 셀에서는 잘리므로 전문은 title 로 남긴다. */}
      {footnote && (
        <div
          className={cn(
            // Figma 8881:798305 — Caption 2/Medium + neutral/400
            "mt-0.5 truncate caption-2-medium text-neutral-400",
            alignRight && "text-right",
          )}
          title={footnote}
        >
          {footnote}
        </div>
      )}
    </IxdTable.Cell>
  );
};

export default TableCell;
